import React, {useState} from "react";

import {useTranslation} from "react-i18next";


import {useSelector} from "react-redux";
import {CompletePayroll, Employee} from "../../../app/interfaces";
import useFetchPayrolls from "../../../hooks/useFetchPayrolls";
import {selectAdminUser} from "../../../app/adminUser/selectors";
import {allPayrollsFetchError, allPayrollsFetchState, selectedPayrollsPaginated} from "../../../app/payroll/selectors";
import {
    notPaidPayrollStatuses,
} from "../../../utils/constants";
import {DataTableLayout, EmptyDataTableLayout} from "../../../layouts/DataTableLayout/DataTableLayout";
import {Dropdown, FlexRow, LoadingComponent, StatusPill, Table} from "../../../components";
import {
    DataTableLayoutFooter,
    DataTableLayoutHeader,
    DataTableLayoutTitle
} from "../../../layouts/DataTableLayout/styles";
import {TableBody, TableHeader, TableNavigation} from "../../../components/Table";
import {TableDataCell, TableHeaderCell, TablePrimaryText, TableRow} from "../../../components/Table/styles";
import {downloadDocument, downloadMultipleCompanioDocuments} from "../../../services/documents";
import {useNavigate} from "react-router-dom";

const itemsPerPage = 3;

const PendingPayrolls = ({employee}: { employee: Employee }) => {

    useFetchPayrolls()
    const admin = useSelector(selectAdminUser)

    const [currentPage, setCurrentPage] = useState<number>(1)

    const payrolls = useSelector(
        selectedPayrollsPaginated(
            currentPage || 1,
            itemsPerPage,
            notPaidPayrollStatuses,
            employee._id,
            null
        )
    )

    const {t} = useTranslation()
    const dataTableTitle = t('pending_payrolls')

    const fetchState = useSelector(allPayrollsFetchState)
    const error = useSelector(allPayrollsFetchError)

    const navigate = useNavigate();

    if (fetchState === 'loading' || fetchState === 'idle') {
        return <EmptyDataTableLayout title={dataTableTitle}>
            <LoadingComponent/>
        </EmptyDataTableLayout>
    }

    if (error) {
        console.warn(error)
        return <EmptyDataTableLayout title={dataTableTitle}>
            {error}
        </EmptyDataTableLayout>
    }

    if (!payrolls.items.length) {
        return <EmptyDataTableLayout
            title={dataTableTitle}>
            {t('no_payrolls') as string}
        </EmptyDataTableLayout>
    }

    const handleOpenPayroll = (payroll: CompletePayroll) => navigate(`/payroll/${payroll._id}`);

    return <DataTableLayout>
        <DataTableLayoutHeader>
            <DataTableLayoutTitle>{t('pending_payrolls')}</DataTableLayoutTitle>
        </DataTableLayoutHeader>
        <Table>
            <TableHeader>
                <TableHeaderCell>{t('date')}</TableHeaderCell>
                <TableHeaderCell style={{textAlign: 'center'}}>{t('status')}</TableHeaderCell>
            </TableHeader>
            <TableBody>
                {payrolls.items.map((p, i) => PayrollRow(p, i, t, admin.locale, handleOpenPayroll))}
            </TableBody>
        </Table>
        <DataTableLayoutFooter>
            <TableNavigation
                pageLimit={itemsPerPage}
                summary={payrolls.summary}
                setPage={setCurrentPage}
            />
        </DataTableLayoutFooter>
    </DataTableLayout>
}

const PayrollRow = (payroll: CompletePayroll, index: number, translate: Function, locale:string = 'en', handleOpenPayroll: Function) => {
    const date = new Date(payroll.created).toLocaleDateString(locale, {month: 'long', year: 'numeric'})

    return <TableRow key={index}>
        <TableDataCell>
            <FlexRow>
                <TablePrimaryText>
                    {date}
                </TablePrimaryText>
            </FlexRow>
        </TableDataCell>
        <TableDataCell>
            <FlexRow justify="center">
                <StatusPill status={payroll.status} style={{display: 'flex', justifyContent: 'center'}}>
                    {translate(payroll.status)}
                </StatusPill>
            </FlexRow>
        </TableDataCell>
        <TableDataCell>
            <FlexRow justify="flex-end">
                <Dropdown items={[
                    {
                        label: translate('see_payroll'),
                        onClick: () => handleOpenPayroll(payroll)
                    },
                    ...(payroll.payslip ? [{
                        label: translate('download_payslip'),
                        onClick: () => downloadDocument(payroll.payslip, payroll.company._id)
                    }] : []),
                    ...(payroll.eor_partner_invoices?.length ? [{
                        label: translate('download_eor_partner'),
                        onClick: () => downloadMultipleCompanioDocuments(payroll.eor_partner_invoices)
                    }] : []),
                    ...(payroll.company_invoice ? [{
                        label: translate('download_company_invoice'),
                        onClick: () => downloadDocument(payroll.company_invoice, payroll.company._id)
                    }] : []),
                ]}/>
            </FlexRow>
        </TableDataCell>
    </TableRow>
}


export default PendingPayrolls;
